import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { User } from "../models/authentication/user";
import { useUserApi } from "../apis/user.api";
import useAuthenticationToken from "../hooks/useAuthenticationToken";

type AuthContextFields = {
  user?: User;
  login: (email: string) => Promise<User>;
  logout: () => void;
  isLoading: boolean;
  error?: Error;
  initialized: boolean;
  loggedIn: boolean;
  autoLogin: () => Promise<void>;
};

const AuthContext = createContext<AuthContextFields>({
  login: () => Promise.resolve({ email: "", roles: [], schoolIds: [] }),
  logout: () => {},
  isLoading: false,
  initialized: false,
  loggedIn: false,
  autoLogin: () => Promise.resolve()
});

export const useAuth = () => useContext(AuthContext);

const useProvideAuth = () => {
  const [user, setUser] = useState<User | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const { getProfile, login: apiLogin, logout: apiLogout } = useUserApi();
  const loggedIn = useMemo(() => !!user, [user]);
  const { getToken } = useAuthenticationToken();

  const getUserProfile = useCallback(
    () =>
      getProfile().then((u) => {
        setUser(u);
        return u;
      }),
    [getProfile],
  );

  const autoLogin = useCallback(async () => {
    const token = getToken();
    if (token) {
      setIsLoading(true);
      await getUserProfile().catch((e) => {
        setError(e);
        return Promise.resolve(undefined);
      });
      setIsLoading(false);
    }
    setInitialized(true);
  }, [getToken, getUserProfile]);

  const login = (email: string) => {
    setIsLoading(true);
    logout();
    return apiLogin(email)
      .then(() => getUserProfile())
      .catch((e) => {
        setError(e);
        return Promise.reject(e);
      })
      .finally(() => setIsLoading(false));
  };

  const logout = () => {
    apiLogout();
    setUser(undefined);
  };

  return {
    user,
    login,
    logout,
    isLoading,
    error,
    initialized,
    loggedIn,
    autoLogin,
  };
};

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const auth = useProvideAuth();

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
